import { useEffect, useRef, useState } from 'react'
import StorageManager from '../services/StorageManager'

/**
 * A custom hook for using persistent state
 * @template T The type of the stored value
 * @param {string} key - the key to be stored in localStorage, must be stable across renders
 * @param {T | (() => T)} defaultValue - can either be a value or a function returning a value (In case there is heavy computation involved)
 * @returns {[T, React.Dispatch<React.SetStateAction<T>>]} - The same structure that React.useState returns
 */
function useLocalStorageState<T>(
  key: string,
  defaultValue: T | (() => T) = '' as T
) {
  const [state, setState] = useState<T>(() => {
    const valueInLocalStorage = window.localStorage.getItem(key)
    if (valueInLocalStorage) {
      // the try/catch is here in case the localStorage value was set before
      try {
        return StorageManager.get<T>(key)!
      } catch (_error) {
        StorageManager.remove(key)
      }
    }
    return typeof defaultValue === 'function'
      ? (defaultValue as () => T)()
      : defaultValue
  })

  const initialKey = useRef(key).current
  if (key !== initialKey) {
    throw new Error(
      `useLocalStorageState key must be stable, got "${key}" after "${initialKey}"`
    )
  }

  useEffect(() => {
    StorageManager.set(key, state)
  }, [key, state])

  return [state, setState] as const
}

export default useLocalStorageState
